import { EpicAuthClient } from '@/features/auth/services/EpicAuthClient'
import { Organization, Patient, Practitioner, Resource } from 'fhir/r4'

interface ContextInfo {
  patient: Patient
  practitioner: Practitioner
  practitioner_npi: string | null
  practitioner_email: string | null
  organization: Organization | null
}

export class FhirR4Client {
  private accessToken: string
  private baseUrl: string
  private headers: Record<string, string>

  constructor(accessToken: string, issuerUrl: string) {
    this.accessToken = accessToken
    this.baseUrl = issuerUrl + '/api/FHIR/R4'
    this.headers = {
      Authorization: `Bearer ${this.accessToken}`,
      'Content-Type': 'application/json',
      accept: 'application/json',
    }
  }

  /**
   * Read a FHIR resource by type and ID
   *
   * @param resourceType - The FHIR resource type (e.g., "Patient", "Practitioner")
   * @param resourceId - The resource ID
   * @returns Promise with the FHIR resource
   */
  async r4Read(resourceType: string, resourceId: string): Promise<Resource> {
    const url = `${this.baseUrl}/${resourceType}/${resourceId}`
    const response = await fetch(url, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`FHIR read failed: ${response.status} ${response.statusText}`)
    }

    return (await response.json()) as Resource
  }

  /**
   * Search FHIR resources by type with parameters
   *
   * @param resourceType - The FHIR resource type
   * @param params - Search parameters
   * @returns Promise with the search results
   */
  async r4Search(resourceType: string, params: Record<string, string>): Promise<Resource> {
    const url = `${this.baseUrl}/${resourceType}`
    const searchParams = new URLSearchParams(params)
    const fullUrl = `${url}?${searchParams.toString()}`

    const response = await fetch(fullUrl, {
      method: 'GET',
      headers: this.headers,
    })

    if (!response.ok) {
      throw new Error(`FHIR search failed: ${response.status} ${response.statusText}`)
    }

    return (await response.json()) as Resource
  }

  /**
   * Get comprehensive context information including patient, practitioner, and organization
   *
   * @param patientId - Patient FHIR ID
   * @param practitionerId - Practitioner FHIR ID
   * @returns Promise with context information including extracted NPI and email
   */
  async getContextInfo(patientId: string, practitionerId: string): Promise<ContextInfo> {
    const patient = (await this.r4Read('Patient', patientId)) as Patient
    const practitioner = (await this.r4Read('Practitioner', practitionerId)) as Practitioner

    let organization: Organization | null = null
    if (patient.managingOrganization?.reference) {
      const organizationId = patient.managingOrganization.reference.split('/')[1]
      if (organizationId) {
        organization = (await this.r4Read('Organization', organizationId)) as Organization
      }
    }

    // Extract NPI from practitioner identifiers
    const npi = this.extractNpi(practitioner)

    // Extract email from practitioner telecom
    const email = this.extractEmail(practitioner)

    return {
      patient,
      practitioner,
      practitioner_npi: npi,
      practitioner_email: email,
      organization,
    }
  }

  /**
   * Extract NPI (National Provider Identifier) from practitioner FHIR resource
   *
   * @param practitioner - FHIR Practitioner resource
   * @returns NPI number if found, null otherwise
   */
  private extractNpi(practitioner: Practitioner): string | null {
    if (!practitioner.identifier) {
      return null
    }

    for (const identifier of practitioner.identifier) {
      // Check if this identifier is an NPI
      if (identifier.type?.coding) {
        for (const coding of identifier.type.coding) {
          if (coding.code === 'NPI' || coding.system === 'http://hl7.org/fhir/sid/us-npi') {
            return identifier.value || null
          }
        }
      }

      // Also check system directly for NPI
      if (identifier.system === 'http://hl7.org/fhir/sid/us-npi') {
        return identifier.value || null
      }
    }

    return null
  }

  /**
   * Extract email address from practitioner FHIR resource telecom
   *
   * @param practitioner - FHIR Practitioner resource
   * @returns Email address if found, null otherwise
   */
  private extractEmail(practitioner: Practitioner): string | null {
    if (!practitioner.telecom) {
      return null
    }

    for (const telecom of practitioner.telecom) {
      // Check if this telecom entry is an email
      if (telecom.system === 'email') {
        return telecom.value || null
      }
    }

    return null
  }

  /**
   * Get the base URL for FHIR API calls
   *
   * @returns The base URL
   */
  getBaseUrl(): string {
    return this.baseUrl
  }

  /**
   * Get the current access token
   *
   * @returns The access token
   */
  getAccessToken(): string {
    return this.accessToken
  }

  /**
   * Update the access token (useful for token refresh)
   *
   * @param newAccessToken - The new access token
   */
  updateAccessToken(newAccessToken: string): void {
    this.accessToken = newAccessToken
    this.headers['Authorization'] = `Bearer ${this.accessToken}`
  }
}

// Types for the form info response
export interface FormInfoResponse {
  patient: Patient
  practitioner: Practitioner
  practitioner_npi: string | null
  practitioner_email: string | null
  organization: Organization | null
}

// Types for errors
export class FormInfoError extends Error {
  constructor(
    message: string,
    public statusCode: number = 500,
    public details?: string
  ) {
    super(message)
    this.name = 'FormInfoError'
  }
}

/**
 * Get form info from the Epic FHIR R4 API
 *
 * Returns patient, practitioner, and organization FHIR resources,
 * plus extracted NPI from practitioner identifiers and email from telecom.
 *
 * @param state - The state parameter for session validation
 * @param client - The EpicAuthClient instance
 * @returns Promise with form info response
 * @throws FormInfoError with appropriate status codes
 */
export async function getFormInfo(state: string, client: EpicAuthClient): Promise<FormInfoResponse> {
  try {
    // Check if state exists in sessions
    if (!client.hasSession(state)) {
      throw new FormInfoError('Invalid state parameter or session expired', 400)
    }

    // Get access token
    const accessToken = client.getAccessToken(state)
    if (!accessToken) {
      throw new FormInfoError('No access token found for this state', 401)
    }

    // Create R4 client and get context
    const issuerUrl = client.getIssuerUrl(state)
    const r4Client = new FhirR4Client(accessToken, issuerUrl)
    const context = client.getContext(state)

    // Check if context has required fields
    if (!context.fhirPatientId || !context.fhirUserId) {
      throw new FormInfoError('Missing patient or user ID in context', 400)
    }

    // Get context info from FHIR API
    const contextInfo = await r4Client.getContextInfo(context.fhirPatientId, context.fhirUserId)

    return {
      patient: contextInfo.patient,
      practitioner: contextInfo.practitioner,
      practitioner_npi: contextInfo.practitioner_npi,
      practitioner_email: contextInfo.practitioner_email,
      organization: contextInfo.organization,
    }
  } catch (error) {
    // Re-throw FormInfoError as-is
    if (error instanceof FormInfoError) {
      throw error
    }

    // Handle other errors
    if (error instanceof Error) {
      console.error(`Unexpected error in getFormInfo: ${error.message}`)
      throw new FormInfoError('Internal server error', 500, error.message)
    }

    // Handle unknown errors
    console.error(`Unknown error in getFormInfo: ${error}`)
    throw new FormInfoError('Internal server error', 500, 'Unknown error occurred')
  }
}
